import * as echarts from 'echarts/core';

import { WAND_TARGET_ATTR, type WandElement, type WandFrame, parseWandTarget } from './wandGeometry';
import { type ChartOption, type PieSector, marksFromChart } from './wandMarks';

function cardOf(node: HTMLElement, titles: Map<string, string>): { id: string; title: string } | null {
  const frame = node.closest<HTMLElement>('[data-lens-card-frame]');
  const id = frame?.getAttribute('data-lens-card-frame');
  if (!id) {
    return null;
  }
  return { id, title: titles.get(id) ?? id };
}

export function harvestDomTargets(root: HTMLElement, titles: Map<string, string>): Array<WandElement> {
  return [...root.querySelectorAll<HTMLElement>(`[${WAND_TARGET_ATTR}]`)].flatMap((node) => {
    const target = parseWandTarget(node.getAttribute(WAND_TARGET_ATTR));
    const card = cardOf(node, titles);
    if (!target || !card) {
      return [];
    }
    const rect = node.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      return [];
    }
    return [
      {
        cardId: card.id,
        cardTitle: card.title,
        kind: target.kind,
        label: target.label,
        value: target.value,
        node,
        hit: {
          shape: 'box' as const,
          box: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
        },
      },
    ];
  });
}

function pieSector(chart: echarts.ECharts, seriesIndex: number, dataIndex: number): PieSector | null {
  const model = (
    chart as unknown as {
      getModel?: () => {
        getSeriesByIndex?: (index: number) => {
          getData?: () => { getItemLayout?: (index: number) => unknown };
        };
      };
    }
  ).getModel?.();
  const layout = model
    ?.getSeriesByIndex?.(seriesIndex)
    ?.getData?.()
    ?.getItemLayout?.(dataIndex) as Partial<PieSector> | null;
  if (
    !layout ||
    typeof layout.cx !== 'number' ||
    typeof layout.cy !== 'number' ||
    typeof layout.r !== 'number' ||
    typeof layout.startAngle !== 'number' ||
    typeof layout.endAngle !== 'number'
  ) {
    return null;
  }
  return {
    cx: layout.cx,
    cy: layout.cy,
    r: layout.r,
    r0: layout.r0,
    startAngle: layout.startAngle,
    endAngle: layout.endAngle,
  };
}

export function harvestChartMarks(root: HTMLElement, titles: Map<string, string>): Array<WandElement> {
  return [...root.querySelectorAll<HTMLElement>('[data-lens-chart]')].flatMap((host) => {
    const card = cardOf(host, titles);
    const chart = echarts.getInstanceByDom(host);
    if (!card || !chart) {
      return [];
    }
    const rect = host.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      return [];
    }
    return marksFromChart(
      {
        getOption: () => chart.getOption() as ChartOption,
        convertToPixel: (finder, value) => chart.convertToPixel(finder, value as never),
        pieSector: (seriesIndex, dataIndex) => pieSector(chart, seriesIndex, dataIndex),
      },
      { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      card,
    );
  });
}

export function harvestWandElements(root: HTMLElement, titles: Map<string, string>): Array<WandElement> {
  return [...harvestDomTargets(root, titles), ...harvestChartMarks(root, titles)];
}

export function readCardFrames(root: HTMLElement, titles: Map<string, string>): Array<WandFrame> {
  return [...root.querySelectorAll<HTMLElement>('[data-lens-card-frame]')].flatMap((frame) => {
    const id = frame.getAttribute('data-lens-card-frame');
    if (!id) {
      return [];
    }
    const rect = frame.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      return [];
    }
    return [
      {
        id,
        title: titles.get(id) ?? id,
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
      },
    ];
  });
}
